import { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Trash2,
  Plus,
  Edit,
  Save,
  X,
  LinkIcon,
} from "lucide-react";

export const LinksManager = () => {
  const { token } = useAuth();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingLink, setEditingLink] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    url: "",
    icon: "🔗",
    description: "",
    category: "social",
    order: 0
  });

  const categories = [
    { value: "content", label: "Official Content" },
    { value: "social", label: "Social Media" },
    { value: "professional", label: "Professional Profiles" },
    { value: "other", label: "Other" }
  ];

  const iconOptions = ["🔗", "🔥", "💫", "🎬", "📸", "🎵", "✓", "💎", "⭐", "💝", "🌐", "📱"];

  const fetchLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinks(response.data);
    } catch (error) {
      toast.error("Failed to load links");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const resetForm = () => {
    setFormData({ name: "", url: "", icon: "🔗", description: "", category: "social", order: 0 });
    setEditingLink(null);
    setShowForm(false);
  };

  const saveLink = async () => {
    if (!formData.name || !formData.url) {
      toast.error("Name and URL are required");
      return;
    }

    try {
      if (editingLink) {
        await axios.put(`${API}/admin/links/${editingLink.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Link updated!");
      } else {
        await axios.post(`${API}/admin/links`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success("Link added!");
      }
      fetchLinks();
      resetForm();
    } catch (error) {
      toast.error("Failed to save link");
    }
  };

  const deleteLink = async (id) => {
    if (!window.confirm("Delete this link?")) return;
    try {
      await axios.delete(`${API}/admin/links/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Link deleted!");
      fetchLinks();
    } catch (error) {
      toast.error("Failed to delete link");
    }
  };

  const editLink = (link) => {
    setFormData({
      name: link.name,
      url: link.url,
      icon: link.icon || "🔗",
      description: link.description || "",
      category: link.category || "social",
      order: link.order || 0
    });
    setEditingLink(link);
    setShowForm(true);
  };

  if (loading) return <div className="flex justify-center py-12"><div className="spinner" /></div>;

  return (
    <div data-testid="admin-links-manager">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="font-heading text-3xl text-charcoal mb-2">Links Manager</h1>
          <p className="font-body text-stone-grey">Manage external links shown on your Links page</p>
        </div>
        <Button onClick={() => setShowForm(true)} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" /> Add Link
        </Button>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="glass rounded-xl p-6 mb-8">
          <div className="flex justify-between items-center mb-6">
            <h3 className="font-heading text-xl text-charcoal">
              {editingLink ? "Edit Link" : "Add New Link"}
            </h3>
            <button onClick={resetForm} className="text-stone-grey hover:text-charcoal">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Instagram"
                className="form-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">URL *</Label>
              <Input
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://..."
                className="form-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Icon</Label>
              <div className="flex flex-wrap gap-2">
                {iconOptions.map((icon) => (
                  <button
                    key={icon}
                    onClick={() => setFormData({ ...formData, icon })}
                    className={`w-10 h-10 rounded-lg text-xl flex items-center justify-center transition-all ${
                      formData.icon === icon ? 'bg-jade-deep text-white' : 'bg-sand/50 hover:bg-sand'
                    }`}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Category</Label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="form-input w-full"
              >
                {categories.map((cat) => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <Label className="font-body text-sm text-stone-grey mb-2 block">Description</Label>
              <Input
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this link"
                className="form-input"
              />
            </div>
            <div>
              <Label className="font-body text-sm text-stone-grey mb-2 block">Display Order</Label>
              <Input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                className="form-input"
              />
            </div>
          </div>

          <div className="flex gap-3">
            <Button onClick={saveLink} className="btn-primary">
              <Save className="w-4 h-4 mr-2" /> {editingLink ? "Update" : "Save"}
            </Button>
            <Button onClick={resetForm} variant="outline" className="btn-secondary">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Links List */}
      {links.length === 0 ? (
        <div className="glass rounded-xl p-12 text-center">
          <LinkIcon className="w-12 h-12 text-taupe mx-auto mb-4" />
          <p className="font-body text-stone-grey">No links yet. Add your first link above.</p>
        </div>
      ) : (
        <div className="space-y-4">
          {categories.map((cat) => {
            const catLinks = links.filter(l => l.category === cat.value);
            if (catLinks.length === 0) return null;
            return (
              <div key={cat.value} className="glass rounded-xl p-6">
                <h3 className="font-heading text-lg text-charcoal mb-4">{cat.label}</h3>
                <div className="space-y-3">
                  {catLinks.map((link) => (
                    <div key={link.id} className="flex items-center justify-between bg-sand/30 rounded-lg p-4">
                      <div className="flex items-center gap-4">
                        <span className="text-2xl">{link.icon}</span>
                        <div>
                          <h4 className="font-body font-medium text-charcoal">{link.name}</h4>
                          <p className="font-body text-xs text-taupe truncate max-w-xs">{link.url}</p>
                          {link.description && (
                            <p className="font-body text-sm text-stone-grey">{link.description}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => editLink(link)}>
                          <Edit className="w-4 h-4 text-jade-text" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => deleteLink(link.id)}>
                          <Trash2 className="w-4 h-4 text-lipstick-red" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

